import { Menu, Tooltip, UnstyledButton } from '@mantine/core';
import { IconChevronDown } from '@tabler/icons-react';
import {
  type MouseEvent,
  type ReactNode,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Logo } from './Logo.js';
import { findMenuTrail, MENU, type MenuItem, menuOpenGroups } from './menu.js';
import classes from './SideNav.module.css';
import { followLink } from './shell-context.js';

/** Sol üst marka bloğu: şirket ayarı; yoksa Felixmate (ADR 0019 §4). */
export interface Brand {
  name: string;
  tagline: string;
  /** Veri adresi (`data:image/...`); yoksa adın baş harfiyle monogram karo. */
  logo?: string | null;
  /**
   * Şirket ayarı yok: Felixmate işareti çizilir. Felix işareti şirket adıyla karışmaz
   * (M6f G1-24).
   */
  product?: boolean;
}

export interface SideNavProps {
  path: string;
  onNavigate: (path: string) => void;
  brand: Brand;
  /** Menünün altı: gemi rozeti ve sürüm satırı. */
  footer: ReactNode;
  /** 1024–1279 px: yalnız ikonlar; gruplar yana açılır. */
  compact?: boolean;
  /** Yetki matrisine göre gizlenen öğe kimlikleri (mimari belge §7.1). */
  hidden?: ReadonlySet<string> | undefined;
  /** Çizilecek menü; varsayılan müşteri menüsü `MENU` (yönetici paneli kendi menüsünü verir; ADR 0039 §14). */
  menu?: readonly MenuItem[] | undefined;
}

/** Menü ikonlarının çizgi kalınlığı (referansta kalın; M6f G1-17). */
const STROKE = 1.9;

/** Etkin öğe görünür tutulurken alt kenardan bırakılan pay (px). */
const BOTTOM_GAP = 16;

/** SSR'da `useLayoutEffect` uyarısı vermez; tarayıcıda çizimden önce çalışır. */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

function visible(items: readonly MenuItem[], hidden: ReadonlySet<string>): MenuItem[] {
  return items.flatMap((item) => {
    // Menüde görünmeyen yol satır olarak çizilmez (ADR 0082)
    if (hidden.has(item.id) || item.offMenu) return [];
    if (!item.children) return [item];
    const children = visible(item.children, hidden);
    return children.length > 0 ? [{ ...item, children }] : [];
  });
}

/** Marka işareti: şirket logosu, şirket adının monogramı ya da (ayar yoksa) Felixmate ikonu. */
function BrandMark({ brand, compact }: { brand: Brand; compact: boolean }) {
  if (brand.logo) return <img src={brand.logo} alt="" className={classes.brandLogo} />;
  if (brand.product) return <Logo inverted withWordmark={false} size={compact ? 36 : 40} />;
  return (
    <span className={classes.monogram} aria-hidden>
      {brand.name.trim().charAt(0).toLocaleUpperCase()}
    </span>
  );
}

/**
 * Sol menü (docs/ekranlar/menu-yapisi.md "Düzen ayrıntıları"): koyu lacivert zemin, üstte marka,
 * ana öğelerde ikon ve açılır ok, alt öğelerde nokta, etkin öğede `nav-active` şerit, altta
 * gemi rozeti ve sürüm. Gemi uygulaması ve web ofis aynı bileşeni kullanır (ADR 0019 §2).
 */
export function SideNav({
  path,
  onNavigate,
  brand,
  footer,
  compact = false,
  hidden = new Set(),
  menu = MENU,
}: SideNavProps) {
  const { t } = useTranslation();
  const trail = findMenuTrail(path, menu);
  // Menüde görünmeyen sayfada ilgili menü satırı etkin görünür (ADR 0082)
  const last = trail.at(-1);
  const activeId = last?.offMenu?.activeAs ?? last?.id;
  const activeGroup = trail.length > 1 ? trail[0]?.id : undefined;
  const activeGroups = new Set(trail.slice(0, -1).map((item) => item.id));
  const baseId = useId();
  const navRef = useRef<HTMLElement>(null);

  // Yol değişince yalnız etkin grup açık kalır; üst düzey sayfada hiçbiri (M6f G1-14). Durum
  // çizimden önce güncellenir: eski grubun bir kare açık görünmesi olmaz.
  const [menuState, setMenuState] = useState(() => menuOpenGroups.initial(path, menu));
  const current = menuOpenGroups.navigate(menuState, path, menu);
  if (current !== menuState) setMenuState(current);
  const open = current.open;
  const toggle = (id: string) => setMenuState((state) => menuOpenGroups.toggle(state, id));

  // Menü kutusuna sığmıyorsa (kayıyorsa) alt köşeyle arasında ince çizgi: yarım kalan öğe gemi
  // bloğunun altına giriyormuş gibi görünmez (ekran cilası). Sığan menüde çizgi yok.
  useIsomorphicLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return;
    const update = () => {
      const overflow = nav.scrollHeight > nav.clientHeight + 1;
      if (overflow) nav.dataset.overflow = '';
      else delete nav.dataset.overflow;
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(nav);
    const list = nav.firstElementChild;
    if (list) observer.observe(list);
    return () => observer.disconnect();
  }, []);

  // Gezinmeden sonra etkin öğe görünür kalır (alt köşeye yapışmaz; M6f G1-13)
  useIsomorphicLayoutEffect(() => {
    const nav = navRef.current;
    const item = nav?.querySelector<HTMLElement>('[aria-current="page"]');
    if (!nav || !item) return;
    const box = nav.getBoundingClientRect();
    const rect = item.getBoundingClientRect();
    if (rect.top < box.top) nav.scrollTop -= box.top - rect.top;
    else if (rect.bottom > box.bottom - BOTTOM_GAP)
      nav.scrollTop += rect.bottom - box.bottom + BOTTOM_GAP;
  }, [path]);

  const label = (item: MenuItem) => t(`menu.${item.id}`);
  const items = visible(menu, hidden);

  const link = (item: MenuItem, kind: 'top' | 'sub', level?: 2 | 3) => {
    const Icon = item.icon;
    const active = item.id === activeId;
    const text = label(item);
    return (
      <a
        href={item.path}
        className={kind === 'top' ? classes.item : classes.sub}
        data-level={level}
        data-active={active || undefined}
        aria-current={active ? 'page' : undefined}
        draggable={false}
        onClick={(event) => followLink(event, item.path, onNavigate)}
      >
        {kind === 'top' && Icon ? (
          <Icon size={22} stroke={STROKE} className={classes.icon} aria-hidden />
        ) : (
          <span className={classes.dot} aria-hidden />
        )}
        <span className={classes.label} title={text}>
          {text}
        </span>
      </a>
    );
  };

  /**
   * Üçüncü düzey grup (Compliance ▸ ISM Forms; plan M8a Task 13): alt öğe gibi noktalı satır,
   * açılır ok; öğeleri bir kademe daha girintili ve küçük noktalı. Düğme klavyeyle açılır.
   */
  const subgroup = (item: MenuItem) => {
    const expanded = open.has(item.id);
    const listId = `${baseId}-${item.id}`;
    const text = label(item);
    return (
      <div className={classes.subgroup} data-open={expanded || undefined}>
        <button
          type="button"
          className={classes.sub}
          data-level="2"
          data-group-active={activeGroups.has(item.id) || undefined}
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => toggle(item.id)}
        >
          <span className={classes.dot} aria-hidden />
          <span className={classes.label} title={text}>
            {text}
          </span>
          <IconChevronDown size={13} stroke={2} className={classes.chevron} aria-hidden />
        </button>
        <ul id={listId} className={classes.grandchildren} data-level="3" hidden={!expanded}>
          {(item.children ?? []).map((child) => (
            <li key={child.id}>{link(child, 'sub', 3)}</li>
          ))}
        </ul>
      </div>
    );
  };

  /** Daraltılmış menünün açılır listesi: alt grup bir etiket ve altında öğeleri. */
  const compactItems = (items: readonly MenuItem[]): ReactNode[] =>
    items.flatMap((child) =>
      child.children
        ? [<Menu.Label key={child.id}>{label(child)}</Menu.Label>, ...compactItems(child.children)]
        : [
            <Menu.Item
              key={child.id}
              component="a"
              href={child.path}
              draggable={false}
              data-active={child.id === activeId || undefined}
              aria-current={child.id === activeId ? 'page' : undefined}
              onClick={(event) =>
                followLink(
                  event as unknown as MouseEvent<HTMLAnchorElement>,
                  child.path,
                  onNavigate,
                )
              }
            >
              {label(child)}
            </Menu.Item>,
          ],
    );

  return (
    <div className={classes.root} data-compact={compact || undefined}>
      <div className={classes.brand}>
        {brand.product && !compact ? (
          // Ürün markası: koyu menüde beyaz kompakt logo, altında açıklama (docs/logo-degisimi.md)
          <span className={classes.productBrand}>
            <Logo inverted size={30} productName={brand.name} />
            {brand.tagline ? (
              <span className={classes.brandTagline} title={brand.tagline}>
                {brand.tagline}
              </span>
            ) : null}
          </span>
        ) : (
          <BrandMark brand={brand} compact={compact} />
        )}
        {compact || brand.product ? null : (
          <span className={classes.brandText}>
            <span className={classes.brandName} title={brand.name}>
              {brand.name}
            </span>
            {brand.tagline ? (
              <span className={classes.brandTagline} title={brand.tagline}>
                {brand.tagline}
              </span>
            ) : null}
          </span>
        )}
      </div>

      <nav ref={navRef} className={classes.nav} aria-label={t('shell.mainMenu')}>
        <ul className={classes.list}>
          {items.map((item) => {
            const Icon = item.icon;
            if (!item.children) {
              return (
                <li key={item.id}>
                  {compact ? (
                    <Tooltip label={label(item)} position="right" withArrow>
                      <a
                        href={item.path}
                        className={classes.item}
                        data-active={item.id === activeId || undefined}
                        aria-current={item.id === activeId ? 'page' : undefined}
                        aria-label={label(item)}
                        draggable={false}
                        onClick={(event) => followLink(event, item.path, onNavigate)}
                      >
                        {Icon ? <Icon size={20} stroke={STROKE} aria-hidden /> : null}
                      </a>
                    </Tooltip>
                  ) : (
                    link(item, 'top')
                  )}
                </li>
              );
            }

            const isActiveGroup = item.id === activeGroup;
            if (compact) {
              return (
                <li key={item.id}>
                  <Menu position="right-start" offset={6} width={240} withinPortal>
                    <Menu.Target>
                      <UnstyledButton
                        className={classes.item}
                        data-group-active={isActiveGroup || undefined}
                        aria-label={label(item)}
                      >
                        {Icon ? <Icon size={20} stroke={STROKE} aria-hidden /> : null}
                      </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Label>{label(item)}</Menu.Label>
                      {compactItems(item.children ?? [])}
                    </Menu.Dropdown>
                  </Menu>
                </li>
              );
            }

            const expanded = open.has(item.id);
            const listId = `${baseId}-${item.id}`;
            const text = label(item);
            return (
              <li key={item.id} className={classes.group} data-open={expanded || undefined}>
                <button
                  type="button"
                  className={classes.item}
                  data-group-active={isActiveGroup || undefined}
                  aria-expanded={expanded}
                  aria-controls={listId}
                  onClick={() => toggle(item.id)}
                >
                  {Icon ? (
                    <Icon size={22} stroke={STROKE} className={classes.icon} aria-hidden />
                  ) : null}
                  <span className={classes.label} title={text}>
                    {text}
                  </span>
                  <IconChevronDown size={14} stroke={2} className={classes.chevron} aria-hidden />
                </button>
                <ul id={listId} className={classes.children} hidden={!expanded}>
                  {(item.children ?? []).map((child) => (
                    <li key={child.id}>
                      {child.children ? subgroup(child) : link(child, 'sub', 2)}
                    </li>
                  ))}
                </ul>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className={classes.footer}>{footer}</div>
    </div>
  );
}
